import Phaser from 'phaser'
import { KENO_NUMBER_STATUS, KenoNumber } from './number'
import { STANDARD_NUMBER_HEIGHT, STANDARD_NUMBER_WIDTH } from '@Animation/constants'
import { store } from '@AppState/redux-store'
import { ANIMATION_IDENTIFIERS } from '@Animation/types'

export class NumberGrid extends Phaser.GameObjects.Container {
  numbers: KenoNumber[] = []
  public xPadding = STANDARD_NUMBER_WIDTH
  public yPadding = STANDARD_NUMBER_HEIGHT

  playerSelectedNumbers: number[]

  standardNumberClass: typeof KenoNumber
  extraDrawNumberClass: typeof KenoNumber | null

  animationIdentifier: ANIMATION_IDENTIFIERS

  glowEffectTween: Phaser.Tweens.Tween | null = null
  glowingNumbers: Phaser.GameObjects.Image[] = []

  constructor(
    scene: Phaser.Scene,
    x: number,
    y: number,
    playerSelectedNumbers: number[],
    kenoNumberClass: typeof KenoNumber,
    extraDrawNumberClass: typeof KenoNumber | null,
  ) {
    super(scene, x, y)
    scene.add.existing(this)

    this.animationIdentifier = store.getState().animation.animationIdentifier

    this.playerSelectedNumbers = playerSelectedNumbers
    this.extraDrawNumberClass = extraDrawNumberClass
    this.standardNumberClass = kenoNumberClass

    this.setVisible(false)
    this.setAlpha(0)
  }

  createGlowTween() {
    this.glowEffectTween = this.scene.tweens.addCounter({
      from: 1.0,
      to: 1.07,
      duration: 500,
      yoyo: true,
      repeat: -1,
      ease: Phaser.Math.Easing.Sine.InOut,
      onUpdate: (tween) => {
        const scaleValue = tween.getValue()
        this.glowingNumbers.forEach((img) => img.setScale(scaleValue))
      },
    })
  }

  addNumberToGlowEffect(glowImage: Phaser.GameObjects.Image) {
    this.glowingNumbers.push(glowImage)
    if (this.glowEffectTween === null) {
      this.createGlowTween()
    }
  }

  async createNumbers() {
    for (let i = 1; i <= 80; i++) {
      const num = await this.createNumber(i)
      this.numbers.push(num)
      this.add(num)
    }

    await Promise.all([this.numbers.map((num) => num.addImage())])
    this.setVisible(true)
  }

  async createNumber(number: number): Promise<KenoNumber> {
    return new Promise((resolve) => {
      const posX = this.xPadding * ((number - 1) % 10)
      const posY = this.yPadding * Math.floor((number - 1) / 10)
      const num = new this.standardNumberClass(
        this.scene,
        posX,
        posY,
        number,
        this.playerSelectedNumbers.includes(number),
        this.animationIdentifier,
      )

      resolve(num)
    })
  }

  async selectNumber(number: number) {
    return await this.numbers[number - 1].select(false)
  }

  async selectExtraNumber(number: number) {
    if (this.extraDrawNumberClass) {
      await this.selectSpecialNumber(number, this.extraDrawNumberClass)
    }
  }

  async selectSpecialNumber(number: number, kenoNumber: typeof KenoNumber) {
    const numberIndex = number - 1
    const numberToReplace = this.numbers[numberIndex]
    this.numbers[numberIndex] = new kenoNumber(
      this.scene,
      numberToReplace.x,
      numberToReplace.y,
      number,
      numberToReplace.status === KENO_NUMBER_STATUS.SELECTED_PLAYER,
      this.animationIdentifier,
    )

    await this.numbers[numberIndex].addImage()

    this.add(this.numbers[numberIndex])
    numberToReplace.destroy()
    await this.numbers[numberIndex].select(false)
  }

  skip(selectedNumbers: number[]) {
    selectedNumbers.forEach((val) => this.numbers[val - 1].skip())
  }

  destroy(fromScene?: boolean): void {
    this.numbers = []
    this.removeAll(true)
    super.destroy(fromScene)
  }
}
